/**
 * Create your account's last step (day 33, in Harvey's look): the profile,
 * and consent, which make the account. Shown when GET /api/me answers
 * needs_profile. The account opens at once, on the Free plan, at the
 * Dashboard (PRD v3.2; day 35).
 *
 * Nothing about the student is stored until this is submitted, and it is
 * submitted with the consent in the same request, because a row with no
 * consent is a row there was no basis to keep. The address is not asked for:
 * it is the one Clerk verified, shown here so the student knows which it is.
 *
 * Ported from the prototype's profile step. Its wording was cut down on 27
 * September 2026 at Mohit's asking: the name field's placeholder now says what
 * the paragraph above it used to (certificates print this name).
 */

import { useState } from "preact/hooks";
import type { Profile, StudentYear } from "@ls/shared";
import { PROGRAMMES, YEAR_LABELS, yearsOf, type ProgrammeId } from "@ls/shared/catalogue";
import { ApiUnavailable, expect, http } from "../api";
import { POLICY_VERSION, createProfileResponseContract, profileErrorContract } from "../contracts.generated";
import { BRAND_NAME } from "../brand";
import { Button, Select } from "../ui";

export interface ProfileStepProps {
  readonly email: string | null;
  /** The account exists now: open (every account since day 35), or waiting for an admin if approval is turned back on. */
  readonly onCreated: (profile: Profile, access: "pending" | "approved") => void;
  /** The API's answer changed underneath the form: ask it again. */
  readonly onRecheck: () => void;
  readonly onUseAnotherAccount: () => void;
}

export function ProfileStep(props: ProfileStepProps) {
  const [name, setName] = useState("");
  const [college, setCollege] = useState("");
  /** Chosen before anything else about the course: the years on offer follow it. */
  const [programme, setProgramme] = useState<ProgrammeId | "">("");
  const [year, setYear] = useState<StudentYear>("1");
  const [city, setCity] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!accepted) {
      setError("Agree to the Terms of Use and Privacy Policy to continue.");
      return;
    }
    setBusy(true);
    setError(null);
    setFields({});
    try {
      const reply = await http.post("/profile", {
        name: name.trim(),
        college: college.trim(),
        programme,
        year,
        city: city.trim(),
        consent: { accepted: true, policyVersion: POLICY_VERSION },
      });
      if (reply.status === 201) {
        const created = expect(reply, createProfileResponseContract, "/profile");
        props.onCreated(created.profile, created.access);
        return;
      }
      const problem = profileErrorContract.safeParse(reply.body);
      if (problem.success && problem.data.error === "invalid_request" && problem.data.fields) {
        setFields(problem.data.fields);
        setError("Check the highlighted fields.");
        return;
      }
      if (problem.success && problem.data.error === "policy_outdated") {
        setError("The policies changed while this page was open. Reload the page to read the current ones.");
        return;
      }
      // An account already exists, from another tab, or the sign in ended:
      // either way the gate knows what to show next.
      props.onRecheck();
    } catch (caught) {
      setError(
        caught instanceof ApiUnavailable
          ? `${BRAND_NAME} could not be reached. Check your connection.`
          : "Something went wrong. Try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  const hint = (field: string) =>
    fields[field] ? (
      <span class="hint auth-field-error" id={`${field}-error`}>
        {fields[field]}
      </span>
    ) : null;

  return (
    <div class="auth-step">
      <p class="eyebrow">Step 2 of 2</p>
      <h1>Your profile</h1>
      {error ? (
        <p class="notice bad auth-alert" role="alert">
          {error}
        </p>
      ) : null}
      <form
        class="auth-form-body"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {props.email ? (
          <div class="field">
            <label for="pemail">Email</label>
            <input class="input" id="pemail" type="email" value={props.email} readOnly />
          </div>
        ) : null}
        <div class="field">
          <label for="pname">Full name</label>
          <input
            class="input"
            id="pname"
            autocomplete="name"
            placeholder="As on your college ID"
            required
            value={name}
            aria-invalid={fields["name"] ? "true" : undefined}
            aria-describedby={fields["name"] ? "name-error" : undefined}
            onInput={(event) => setName((event.currentTarget as HTMLInputElement).value)}
          />
          {hint("name")}
        </div>
        <div class="field">
          <label for="pcollege">College</label>
          <input
            class="input"
            id="pcollege"
            autocomplete="organization"
            required
            placeholder="e.g. NLSIU, Bangalore"
            value={college}
            aria-invalid={fields["college"] ? "true" : undefined}
            aria-describedby={fields["college"] ? "college-error" : undefined}
            onInput={(event) => setCollege((event.currentTarget as HTMLInputElement).value)}
          />
          {hint("college")}
        </div>
        <div class="grid g2">
          <div class="field">
            <label for="pprogramme">Programme</label>
            <Select
              id="pprogramme"
              value={programme}
              placeholder="Choose"
              invalid={Boolean(fields["programme"])}
              options={PROGRAMMES.map((option) => ({ value: option.id, label: option.label }))}
              onChange={(value) => {
                const next = value as ProgrammeId;
                setProgramme(next);
                // A year the new programme does not have gives way to its last one.
                if (!yearsOf(next).includes(year)) setYear(yearsOf(next).at(-1) as StudentYear);
              }}
            />
            {hint("programme")}
          </div>
          <div class="field">
            <label for="pyear">Year</label>
            <Select
              id="pyear"
              value={year}
              disabled={programme === ""}
              options={(programme === "" ? (["1"] as const) : yearsOf(programme)).map((value) => ({ value, label: YEAR_LABELS[value] ?? value }))}
              onChange={(value) => setYear(value as StudentYear)}
            />
            {hint("year")}
          </div>
        </div>
        <div class="field">
          <label for="pcity">City</label>
          <input
            class="input"
            id="pcity"
            autocomplete="address-level2"
            required
            value={city}
            aria-invalid={fields["city"] ? "true" : undefined}
            aria-describedby={fields["city"] ? "city-error" : undefined}
            onInput={(event) => setCity((event.currentTarget as HTMLInputElement).value)}
          />
          {hint("city")}
        </div>
        <label class="auth-consent">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(event) => setAccepted((event.currentTarget as HTMLInputElement).checked)}
          />
          <span>I accept the Terms of Use and Privacy Policy</span>
        </label>
        <Button variant="primary" type="submit" block disabled={busy}>
          {busy ? "Creating your account..." : "Create your account"}
        </Button>
        <div class="auth-row">
          <Button variant="quiet" size="sm" onClick={props.onUseAnotherAccount}>
            Use a different email
          </Button>
        </div>
      </form>
    </div>
  );
}
